import Pagehead from '@oberoninternal/travelbase-ds/components/section/Pagehead';
import dateTextFormat from '@oberoninternal/travelbase-ds/constants/dateTextFormat';
import { format } from 'date-fns';
import { debounce } from 'debounce';
import { Formik } from 'formik';
import gql from 'graphql-tag';
import isEqual from 'lodash/isEqual';
import React, { FC, memo, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { areEqual, ListChildComponentProps } from 'react-window';
import styled from 'styled-components/macro';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import { getDateOpts } from '../../constants/dateOpts';
import createTable, { CreateRow } from '../../hooks/useBigList';
import BigListWrapper from '../atoms/BigListWrapper';
import ContentWrapper from '../atoms/ContentWrapper';
import Effect from '../atoms/Effect';
import BigList from '../molecules/BigList';
import ActivityDetails from '../atoms/ActivityDetails';
import { ActivityReviewFragment, useActivityReviewsQuery } from '../../generated/graphql';

export const query = gql`
    query ActivityReviews($partnerId: ID!, $activityIds: [ID!]) {
        partner(id: $partnerId) {
            id
            activityReviews(activityIds: $activityIds) {
                ...ActivityReview
            }
        }
    }

    fragment ActivityReview on ActivityReview {
        id
        title
        createdAt
        average
        enabled
        language
        city
        reply
        activity{
            ...ActivityPickerEntry
        }
    }
`;

interface ActivityReviewItemProps extends ListChildComponentProps {
    data: {
        createRow: CreateRow;
        activityReviews: ActivityReviewFragment[];
    };
}

const widths = [[3 / 16, null, 2 / 8], [3 / 16, null, 1 / 8], [1 / 8], [3 / 16, null, 1 / 8], 1 / 8, 1 / 8, 1 / 8];

const ActivityReviewItem: FC<React.PropsWithChildren<ActivityReviewItemProps>> = memo(
    ({ index, style, data: { createRow, activityReviews } }) => {
        const activityReview = activityReviews[index];
        const { pathname } = useLocation();
        const { id, title, city, activity, createdAt, average, reply, enabled } = activityReview;

        return createRow(`${pathname}/${id}`, style, [
            <StyledActivityDetails key={id} entity={{ ...activity }} />,
            format(new Date(createdAt), dateTextFormat, getDateOpts('nl')),
            city ?? 'Onbekend',
            title,
            average,
            reply ? 'Ja' : 'Nee',
            enabled ? 'Nee' : 'Ja',
        ]);
    },
    areEqual
);

const ActivityReviews: FC<React.PropsWithChildren<unknown>> = () => {
    const { partnerId } = useParams<{ partnerId: string }>();
    const { formatMessage } = useIntl();
    const [paramQuery, setQuery] = useQueryParams({
        activityIds: withDefault(ArrayParam, []),
    });

    const { data, loading, refetch } = useActivityReviewsQuery({ variables: { partnerId, activityIds: [] } });
    const activityReviews = data?.partner?.activityReviews ?? [];
    const [isSearching, setIsSearching] = useState(false);

    const { createHeader, createRow, createSkeletonRows } = createTable(widths);

    // TODO: use useMemo instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const performSearch = useCallback(
        debounce(async (variables: { activityIds: string[] }) => {
            await refetch({ partnerId, ...variables });
            setIsSearching(false);

            // reflect the search params in the url. Also set the value to undefined if null, cause that's what the library expects
            setQuery({ activityIds: variables.activityIds });
        }, 600),
        [refetch]
    );

    return (
        <Formik
            onSubmit={async values => {
                setIsSearching(true);
                await performSearch(values);
            }}
            initialValues={{ activityIds: paramQuery.activityIds as string[] }}
        >
            {formikBag => (
                <ContentWrapper>
                    <Effect<{ activityIds: string[] }>
                        onChange={({ values: nextValues }, prevState) => {
                            const prevValues = prevState?.values ?? nextValues;
                            if (!isEqual(prevValues, nextValues)) {
                                formikBag.submitForm();
                            }
                        }}
                    />
                    <Pagehead title={formatMessage({ defaultMessage: 'Beoordelingen' })}>
                        <FormattedMessage defaultMessage="Hier kun je de beoordelingen op jouw activiteiten(s) bekijken." />
                    </Pagehead>
                    <BigListWrapper>
                        {createHeader([
                            <FormattedMessage key={partnerId} defaultMessage="Activiteit" />,
                            <FormattedMessage key={partnerId} defaultMessage="Aangemaakt op" />,
                            <FormattedMessage key={partnerId} defaultMessage="Beoordelingstitel" />,
                            <FormattedMessage key={partnerId} defaultMessage="Woonplaats" />,
                            <FormattedMessage key={partnerId} defaultMessage="Gemiddelde" />,
                            <FormattedMessage key={partnerId} defaultMessage="Beantwoord" />,
                            <FormattedMessage key={partnerId} defaultMessage="Geblokkeerd" />,
                        ])}
                        {!isSearching && !loading &&  activityReviews.length > 0 ? (
                            <BigList itemData={{ activityReviews, createRow }} itemSize={78} itemCount={activityReviews.length}>
                                {ActivityReviewItem}
                            </BigList>
                        ) : (
                            createSkeletonRows()
                        )}
                    </BigListWrapper>
                </ContentWrapper>
            )}
        </Formik>
    );
};

export default ActivityReviews;

const StyledActivityDetails = styled(ActivityDetails)`
    min-width: 100%;

    @media (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        > :first-child {
            display: none;
        }
    }
`;
